import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import './Experience.css';

// Import company logos
import tomtaLogo from '../assets/tomta-logo.png';
import pridatexLogo from '../assets/pridatex-logo.png';
import visorwareLogo from '../assets/visorware-logo.PNG';

// IBM Logo SVG Component
const IBMLogo = ({ className }) => (
  <svg className={className} viewBox="0 0 100 40" fill="currentColor">
    <g>
      {/* I */}
      <rect x="0" y="0" width="20" height="4"/>
      <rect x="0" y="6" width="20" height="4"/>
      <rect x="6" y="12" width="8" height="4"/>
      <rect x="6" y="18" width="8" height="4"/>
      <rect x="6" y="24" width="8" height="4"/>
      <rect x="0" y="30" width="20" height="4"/>
      <rect x="0" y="36" width="20" height="4"/>
      {/* B */}
      <rect x="26" y="0" width="24" height="4"/>
      <rect x="26" y="6" width="24" height="4"/>
      <rect x="32" y="12" width="8" height="4"/>
      <rect x="44" y="12" width="6" height="4"/>
      <rect x="32" y="18" width="18" height="4"/>
      <rect x="32" y="24" width="8" height="4"/>
      <rect x="46" y="24" width="6" height="4"/>
      <rect x="26" y="30" width="26" height="4"/>
      <rect x="26" y="36" width="26" height="4"/>
      {/* M */}
      <rect x="58" y="0" width="42" height="4"/>
      <rect x="58" y="6" width="42" height="4"/>
      <rect x="64" y="12" width="8" height="4"/>
      <rect x="86" y="12" width="8" height="4"/>
      <rect x="64" y="18" width="8" height="4"/>
      <rect x="76" y="18" width="6" height="4"/>
      <rect x="86" y="18" width="8" height="4"/>
      <rect x="64" y="24" width="8" height="4"/>
      <rect x="76" y="24" width="6" height="4"/>
      <rect x="86" y="24" width="8" height="4"/>
      <rect x="58" y="30" width="14" height="4"/>
      <rect x="76" y="30" width="6" height="4"/>
      <rect x="86" y="30" width="14" height="4"/>
      <rect x="58" y="36" width="14" height="4"/>
      <rect x="86" y="36" width="14" height="4"/>
    </g>
  </svg>
);

// DCG Systems Logo SVG Component  
const DCGLogo = ({ className }) => (
  <svg className={className} viewBox="0 0 140 40" fill="currentColor">
    <text x="0" y="28" fontFamily="Arial Black, sans-serif" fontSize="24" fontWeight="bold">DCG</text>
    <text x="60" y="28" fontFamily="Arial, sans-serif" fontSize="14" fontWeight="normal">Systems</text>
  </svg>
);

const Experience = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const experiences = [
    {
      company: 'IBM',
      role: 'Product Manager',
      team: 'Software Networking - Incubation Team',
      period: 'Oct 2024 – Present',
      location: 'Hybrid',
      logo: null,
      LogoComponent: IBMLogo,
      highlights: [
        'Leading product strategy for next-generation GenAI technologies emerging from IBM Research, driving client adoption and market positioning',
        'Spearheading technical evaluation of GenAI for 4G RAN network congestion prediction, architecting solutions to enhance telecom congestion control software',
        'Driving cross-functional collaboration with IBM Research to translate Time Series-based GenAI innovations under WatsonX into production-ready client solutions',
        'Leading technical design of numerical data projection models for private equity firms, delivering accurate financial asset value predictions',
      ],
      technologies: ['WatsonX', 'Time Series', 'GenAI', 'Python', '4G RAN Networks', 'Data Analytics'],
    },
    {
      company: 'TomtA.ai',
      subtitle: '(formerly Pridatex Corp.)',
      role: 'Cofounder and Head of Engineering',
      team: 'Data Privacy Generative AI Platform',
      period: 'Nov 2020 – Oct 2024',
      location: 'San Francisco, CA',
      patent: 'US 62822850',
      logo: tomtaLogo,
      LogoComponent: null,
      highlights: [
        'Led end-to-end delivery of 5 successful enterprise POCs with US and European customers including government health organizations, large medical device companies, financial credit scoring firms, and U.S. biotech firms requiring GDPR compliance',
        'Architected and implemented novel Differential Privacy + Generative AI hybrid algorithms using Python and Scikit-Learn, achieving 30% privacy improvement and 10% accuracy gains',
        'Led technical strategy for marketplace deployment, deploying algorithms via Docker onto Databricks, expanding client reach by 2x',
        'Designed and built full-stack (MERN) web application + Hybrid Cloud AWS architecture (SQS, EC2, RDS, Lambda) for enterprise-grade integration',
        'Managed and mentored engineering team of 4-5 interns, overseeing frontend development, QA testing, and technical skill development',
      ],
      technologies: ['Python', 'Scikit-Learn', 'Docker', 'AWS', 'MERN Stack', 'Databricks', 'Differential Privacy'],
    },
    {
      company: 'Pridatex Corporation',
      role: 'Co-founder and CEO',
      team: 'Data Privacy Software (DeepTech in Differential Privacy)',
      period: 'Mar 2018 – Oct 2020',
      location: 'San Francisco, CA',
      patent: 'US 62822850',
      logo: pridatexLogo,
      LogoComponent: null,
      highlights: [
        'Co-invented and led development of patented Differential Privacy algorithm using Python and Scikit-learn, ensuring data privacy without sacrificing analytical utility',
        'Drove company vision and go-to-market strategy, securing acceptance into Plug and Play Insurtech Batch 8 accelerator program',
        'Negotiated and closed strategic partnership with BigID for integration with their enterprise Data Discovery Platform',
        'Led cross-platform engineering efforts across Linux, Windows, and Mac, directing AWS EC2 deployment strategy',
        'Architected database integration layer supporting IBM DB2, Oracle, MariaDB, and MySQL for enterprise compatibility',
      ],
      technologies: ['Python', 'C++', 'Scikit-Learn', 'AWS EC2', 'SQL', 'IBM DB2', 'Oracle', 'MariaDB'],
    },
    {
      company: 'Visorware',
      role: 'Founder & CEO',
      team: 'Image Processing and Computer Vision Software for HUDs/AR',
      period: 'Jun 2016 – Mar 2018',
      location: 'West Lafayette, IN',
      patent: 'US 62787377',
      logo: visorwareLogo,
      LogoComponent: null,
      pressLink: 'https://www.pcmag.com/opinions/eyes-on-with-heads-up-display-car-tech',
      pressName: 'Featured in PCMag',
      mediaLinks: [
        { type: 'video', name: 'HUD Demo', url: 'https://www.youtube.com/watch?v=C0Qx2tUBySE' },
        { type: 'video', name: 'Continental Demo', url: 'https://www.youtube.com/watch?v=SrSVCm1PKrA' },
      ],
      highlights: [
        'Invented and patented novel Image Processing algorithm using color-change technology to enhance HUD visibility and safety, reducing visibility-related accidents by 30%',
        'Led full-stack algorithm development in C/C++ with OpenCV, implementing advanced Image Segmentation techniques for HUD and AR applications',
        'Directed business development efforts, securing interest from Continental and AR startups; achieved PCMag press coverage through strategic positioning',
        'Oversaw multi-platform deployment strategy, leading Android OS and Raspberry Pi (Linux) implementations for industrial safety applications',
      ],
      technologies: ['C/C++', 'OpenCV', 'Deep Learning', 'Neural Networks', 'Git', 'Android', 'Raspberry Pi', 'AI Product Leadership'],
    },
    {
      company: 'DCG Systems Inc.',
      subtitle: '(now FEI)',
      role: 'Image Processing Algorithms Development Intern',
      team: 'Microchip Defect Detection - Semiconductor Inspection Systems',
      period: 'May 2015 – Aug 2015',
      location: 'Fremont, CA',
      logo: null,
      LogoComponent: DCGLogo,
      highlights: [
        'Led development of camera control and image processing algorithms in C# and MATLAB, significantly improving microchip defect detection accuracy and inspection efficiency',
        'Designed and implemented predictive GUI using statistical analysis, creating intuitive UX that anticipated user behavior patterns',
        'Pioneered first Invention Alert Form disclosure, successfully licensing innovation to the company—demonstrating early technical leadership and IP creation skills',
        'Partnered with PhD researchers on cutting-edge image processing algorithms, contributing to advancements in semiconductor inspection technology',
      ],
      technologies: ['C#', 'MATLAB', 'Image Processing', 'Camera Systems', 'GUI Development', 'Statistical Analysis'],
    },
  ];

  return (
    <section id="experience" className="experience section" ref={ref}>
      <div className="container">
        <motion.div
          className="section-title"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2>Work Experience</h2>
          <p className="section-subtitle">
            10+ years building enterprise AI solutions
          </p>
        </motion.div>

        <div className="experience-layout">
          {/* Timeline Navigation */}
          <motion.div
            className="experience-nav"
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {experiences.map((exp, index) => (
              <button
                key={index}
                className={`exp-nav-item ${activeIndex === index ? 'active' : ''}`}
                onClick={() => setActiveIndex(index)}
              >
                {exp.logo ? (
                  <img src={exp.logo} alt={exp.company} className="exp-nav-logo" />
                ) : exp.LogoComponent ? (
                  <exp.LogoComponent className="exp-nav-logo-svg" />
                ) : (
                  <span className="exp-nav-company">{exp.company}</span>
                )}
                <span className="exp-nav-role">{exp.role}</span>
                <span className="exp-nav-period">{exp.period}</span>
              </button>
            ))}
          </motion.div>

          {/* Experience Details */}
          <div className="experience-content">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeIndex}
                className="exp-detail"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.4 }}
              >
                <div className="exp-header">
                  {(experiences[activeIndex].logo || experiences[activeIndex].LogoComponent) && (
                    <div className="exp-logo-wrapper">
                      {experiences[activeIndex].logo ? (
                        <img src={experiences[activeIndex].logo} alt={experiences[activeIndex].company} className="exp-logo" />
                      ) : experiences[activeIndex].LogoComponent ? (
                        (() => {
                          const LogoComp = experiences[activeIndex].LogoComponent;
                          return <LogoComp className="exp-logo-svg" />;
                        })()
                      ) : null}
                    </div>
                  )}
                  <div className="exp-title-group">
                    <h3 className="exp-role">{experiences[activeIndex].role}</h3>
                    <div className="exp-company-line">
                      <span className="exp-company">{experiences[activeIndex].company}</span>
                      {experiences[activeIndex].subtitle && (
                        <span className="exp-subtitle">{experiences[activeIndex].subtitle}</span>
                      )}
                    </div>
                    <p className="exp-team">{experiences[activeIndex].team}</p>
                  </div>
                  <div className="exp-meta">
                    <span className="exp-period">{experiences[activeIndex].period}</span>
                    <span className="exp-location">{experiences[activeIndex].location}</span>
                    {experiences[activeIndex].patent && (
                      <span className="exp-patent">Patent: {experiences[activeIndex].patent}</span>
                    )}
                  </div>
                </div>

                {/* Media Links Row */}
                {(experiences[activeIndex].pressLink || experiences[activeIndex].mediaLinks) && (
                  <div className="exp-media-links">
                    {experiences[activeIndex].pressLink && (
                      <a 
                        href={experiences[activeIndex].pressLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="exp-media-link press"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12">
                          <path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2Zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2"/>
                        </svg>
                        {experiences[activeIndex].pressName}
                      </a>
                    )}
                    {experiences[activeIndex].mediaLinks && experiences[activeIndex].mediaLinks.map((media, i) => (
                      <a 
                        key={i}
                        href={media.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="exp-media-link video"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12">
                          <polygon points="5 3 19 12 5 21 5 3"/>
                        </svg>
                        {media.name}
                      </a>
                    ))}
                  </div>
                )}

                <ul className="exp-highlights">
                  {experiences[activeIndex].highlights.map((highlight, i) => (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.1 }}
                    >
                      {highlight}
                    </motion.li>
                  ))}
                </ul>

                <div className="exp-technologies">
                  <span className="tech-label">Technologies:</span>
                  <div className="tech-tags">
                    {experiences[activeIndex].technologies.map((tech, i) => (
                      <span key={i} className="tech-tag">{tech}</span>
                    ))}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Mobile Timeline */}
        <div className="experience-mobile">
          {experiences.map((exp, index) => (
            <motion.div
              key={index}
              className="exp-card-mobile"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className="exp-card-header">
                {exp.logo ? (
                  <img src={exp.logo} alt={exp.company} className="exp-logo-mobile" />
                ) : exp.LogoComponent ? (
                  <exp.LogoComponent className="exp-logo-mobile-svg" />
                ) : null}
                <h3>{exp.role}</h3>
                <div className="exp-company-line">
                  <span className="exp-company">{exp.company}</span>
                  {exp.subtitle && <span className="exp-subtitle">{exp.subtitle}</span>}
                </div>
                <p className="exp-team">{exp.team}</p>
                <div className="exp-meta-mobile">
                  <span>{exp.period}</span>
                  <span>{exp.location}</span>
                </div>
              </div>
              <ul className="exp-highlights-mobile">
                {exp.highlights.slice(0, 3).map((h, i) => (
                  <li key={i}>{h}</li>
                ))}
              </ul>
              <div className="tech-tags">
                {exp.technologies.slice(0, 5).map((tech, i) => (
                  <span key={i} className="tech-tag">{tech}</span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;

